import React, { useState } from "react";
import {
  View,
  StyleSheet,
  Text,
  ScrollView,
  TouchableOpacity,
  Image,
  Modal,
} from "react-native";
import { TGetMessagesResponse } from "../../../../services/api-clinic";
import { SmallCarret } from "../../../../assets";
import { capitalizeString } from "../../../../utils/functions";
import MessageThreadImageModalContent from "./MessageThreadImageModalContent";

type MessageThreadPatientInfoProps = {
  patientData: TGetMessagesResponse["patient_info"];
  patientImages: string[];
  patientImagesOriginalSize: string[];
};

const MessageThreadPatientInfo: React.FC<MessageThreadPatientInfoProps> = ({
  patientData,
  patientImages,
  patientImagesOriginalSize,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  return (
    <View style={styles.wrapper}>
      <View style={styles.patientInfoContainer}>
        <View style={styles.header}>
          <Text style={styles.patientName}>
            {`${patientData.first_name} ${patientData.last_name}`}
          </Text>
          <TouchableOpacity onPress={() => setIsExpanded(!isExpanded)}>
            <SmallCarret
              color={"#00b7f4"}
              direction={isExpanded ? "up" : "down"}
            />
          </TouchableOpacity>
        </View>

        {isExpanded &&
          [
            {
              label: "Date created:",
              value: patientData.created_at.split("T")[0],
            },
            { label: "Phone:", value: patientData.phone },
            {
              label: "Procedures:",
              value: patientData.procedures_of_interest
                .map((procedure, index, array) =>
                  index === array.length - 1
                    ? `${capitalizeString(procedure)}.`
                    : capitalizeString(procedure),
                )
                .join(", "),
            },
          ].map(({ label, value }, index) => (
            <View key={index} style={styles.detailRow}>
              <Text style={styles.label}>{label}</Text>
              <Text style={styles.value}> {value}</Text>
            </View>
          ))}
      </View>

      {isExpanded && (
        <View style={{ flex: 1 }}>
          <ScrollView horizontal contentContainerStyle={styles.imageScrollView}>
            {patientImages.map((_, index) => {
              if (index % 2 !== 0) return null; // Skip odd indices
              return (
                <View key={index} style={styles.imageColumn}>
                  {patientImages[index] && (
                    <TouchableOpacity
                      onPress={() => setSelectedImage(index)}
                      style={styles.imageWrapper}
                    >
                      <Image
                        source={{ uri: patientImages[index] }}
                        style={styles.image}
                      />
                    </TouchableOpacity>
                  )}
                  {patientImages[index + 1] && (
                    <TouchableOpacity
                      onPress={() => setSelectedImage(index + 1)}
                      style={styles.imageWrapper}
                    >
                      <Image
                        source={{ uri: patientImages[index + 1] }}
                        style={styles.image}
                      />
                    </TouchableOpacity>
                  )}
                </View>
              );
            })}
          </ScrollView>
        </View>
      )}
      <Modal visible={!!selectedImage} transparent animationType="fade">
        {selectedImage ? (
          <MessageThreadImageModalContent
            image_initial_index={selectedImage}
            image_urls={patientImagesOriginalSize}
            handleModalClose={() => setSelectedImage(null)}
          />
        ) : null}
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    flexDirection: "row",
    backgroundColor: "#A1E3CB",
    borderRadius: 10,
    flex: 1,
    gap: 30,
    justifyContent: "flex-start",
    alignItems: "flex-start",
    padding: 5,
    minWidth: 250,
    overflow: "hidden",
  },
  patientInfoContainer: {
    flex: 2,
    flexDirection: "column",
    gap: 5,
  },
  header: {
    flexDirection: "row",
    gap: 10,
    alignItems: "center",
  },
  patientName: {
    fontSize: 24,
    fontFamily: "Urbanist600",
    textTransform: "capitalize",
  },
  detailRow: {
    flexDirection: "row",
  },
  label: {
    fontSize: 14,
    fontFamily: "Urbanist600",
  },
  value: {
    fontSize: 14,
    fontFamily: "Urbanist400",
    maxWidth: 300,
  },
  imageScrollView: {
    flexDirection: "row",
  },
  imageColumn: {
    flexDirection: "column",
  },
  imageWrapper: {
    width: 66,
    height: 66,
    borderRadius: 2,
    overflow: "hidden",
    margin: 1,
    backgroundColor: "lightgray",
    justifyContent: "center",
    alignItems: "center",
  },
  image: {
    width: "100%",
    height: "100%",
    resizeMode: "contain",
  },
});

export default MessageThreadPatientInfo;
